// TimeGridView.tsx — Wrapper um OutlookKalender (RBC-Engine). View/Datum/Navigation
// kommen aus dem CalendarStateContext; die RBC-interne Toolbar ist aus
// (toolbar={false}), gesteuert wird von außen über die eigene Top-Leiste.
//
// SP1: events kommen als Prop (in SP1 = [] von CalendarView); kein Event-Load
// hier. onSelectEvent (SP2) öffnet das Detail-Popup.
// SP3 (Drag-to-Create): onSelectSlot (normalisiert zu {start,end,allDay}) und
// Reschedule-Durchreichung (onEventDrop/onEventResize).
// SP3 (Wochenansicht-Review, Entscheidung 61): Ganztagstermine/Abwesenheiten
// laufen über die NATIVE RBC-All-Day-Zeile (allDayAccessor + allDayMaxRows in
// OutlookKalender) — mehrtägige Termine spannen dort als Leiste über ihre
// Tagesspalten. Der frühere Split in eine tageslose Chip-Leiste (AllDayBar)
// ist entfallen; ALLE Events gehen an RBC.

import React, { useCallback, useMemo } from "react";
import { View, Views } from "react-big-calendar";
import OutlookKalender, { KalenderEventChange } from "../OutlookKalender";
import type { SlotInfo } from "react-big-calendar";
import { CalendarView, useCalendarState } from "./CalendarStateContext";
import { normalizeSlot } from "./allDay";
import { DEFAULT_ZEITSKALA, rasterFuerSkala } from "./zeitskala";
import { useResponsive } from "../../hooks/useResponsive";
import type { CalendarGridEvent } from "./types";

export interface TimeGridViewProps {
    /** Bereits gemappte View-Events. SP1: leere Liste; SP5 füllt sie. */
    events: CalendarGridEvent[];
    /**
     * Einzelklick auf einen Termin → seit SP4 Peek-Popover (A16). Das zweite
     * Argument ist das RBC-Klick-Event (Anker-Ableitung im Aufrufer).
     */
    onSelectEvent?: (event: CalendarGridEvent, e?: React.SyntheticEvent) => void;
    /** Doppelklick auf einen Termin → direkt das Termin-Fenster (SP4). */
    onDoubleClickEvent?: (event: CalendarGridEvent) => void;
    /** Rechtsklick auf einen Terminblock → Kontextmenü (SP4, A14). */
    onEventContextMenu?: (event: CalendarGridEvent, pos: { x: number; y: number }) => void;
    /**
     * Leerer Slot aufgezogen → Drag-to-Create (SP3). Die Shape ist bereits
     * normalisiert ({start,end,allDay}, Konsistenz-Review A2): TimeGridView
     * wandelt den rohen RBC-SlotInfo intern um, bevor dieser Contract bedient
     * wird. CalendarView verbindet ihn mit openTerminFormWindow.
     */
    onSelectSlot?: (slot: { start: Date; end: Date; allDay: boolean }) => void;
    /** Termin verschoben (SP3 — Reschedule-Persistenz in CalendarView). */
    onEventDrop?: (change: KalenderEventChange) => void;
    /** Termin in der Größe geändert (SP3). */
    onEventResize?: (change: KalenderEventChange) => void;
    /** WRITE-Gating je Event (SP3 MVP mailbox-basiert; SP5 calendarAccess). */
    canEditEvent?: (event: CalendarGridEvent) => boolean;
}

/** CalendarView-Enum → RBC-View. */
const rbcView = (v: CalendarView): View =>
    v === "day" ? Views.DAY : v === "week" ? Views.WEEK : Views.MONTH;

/** RBC-View → CalendarView-Enum (für onView). */
const fromRbcView = (v: View): CalendarView =>
    v === Views.DAY ? "day" : v === Views.WEEK ? "week" : "month";

const TimeGridView: React.FC<TimeGridViewProps> = ({
    events,
    onSelectEvent,
    onDoubleClickEvent,
    onEventContextMenu,
    onSelectSlot,
    onEventDrop,
    onEventResize,
    canEditEvent,
}) => {
    const { view, currentDate, setCurrentDate, setView, zeitskala } = useCalendarState();
    const { isPhone } = useResponsive();
    // Zeitskala (T-31): nur am Desktop. Auf dem Phone bleibt bewusst das
    // 15-min-Raster — ein kompaktes Raster ist mit dem Finger kaum treffbar.
    const raster = useMemo(
        () => rasterFuerSkala(isPhone ? DEFAULT_ZEITSKALA : zeitskala),
        [isPhone, zeitskala],
    );

    const handleView = useCallback((v: View) => setView(fromRbcView(v)), [setView]);

    // RBC liefert hier einen rohen SlotInfo; TimeGridView normalisiert ihn zu der
    // nach außen versprochenen {start,end,allDay}-Shape (Konsistenz-Review A2),
    // BEVOR der SP1-onSelectSlot-Contract bedient wird. Mindestdauer (30 min bei
    // Klick), All-Day-Erkennung und Monatsklick-Default stecken in normalizeSlot.
    // SP1 verdrahtet onSelectSlot nicht → der Handler bleibt undefined; SP3 nutzt
    // ihn (CalendarView → openTerminFormWindow).
    const handleSelectSlot = useMemo(
        () =>
            onSelectSlot
                ? (slot: SlotInfo) => {
                      // Nur echte Selektion/Klick öffnet ein Fenster; ein per ESC
                      // abgebrochener Drag ruft onSelectSlot gar nicht (RBC-nativ).
                      if (slot.action !== "select" && slot.action !== "click" && slot.action !== "doubleClick")
                          return;
                      onSelectSlot(normalizeSlot(slot, view));
                  }
                : undefined,
        [onSelectSlot, view],
    );

    return (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                <OutlookKalender
                    // ALLE Events an RBC: Ganztägige/Abwesenheiten landen über den
                    // allDayAccessor in der nativen All-Day-Zeile (Tag/Woche) bzw.
                    // im Monatsraster; ein Klick in die leere All-Day-Zeile läuft
                    // als ≥24h-Mitternachts-Slot durch normalizeSlot → allDay-Anlage.
                    events={events}
                    date={currentDate}
                    view={rbcView(view)}
                    onView={handleView}
                    onNavigate={setCurrentDate}
                    toolbar={false}
                    step={raster.step}
                    timeslots={raster.timeslots}
                    stundePx={raster.stundePx}
                    zeitzeileAbMinuten={raster.zeitzeileAbMinuten}
                    onSelectEvent={(e, domEvent) =>
                        onSelectEvent?.(e as CalendarGridEvent, domEvent)
                    }
                    onDoubleClickEvent={
                        onDoubleClickEvent
                            ? (e) => onDoubleClickEvent(e as CalendarGridEvent)
                            : undefined
                    }
                    onEventContextMenu={
                        onEventContextMenu
                            ? (e, pos) => onEventContextMenu(e as CalendarGridEvent, pos)
                            : undefined
                    }
                    onSelectSlot={handleSelectSlot}
                    onEventDrop={onEventDrop}
                    onEventResize={onEventResize}
                    canEditEvent={
                        canEditEvent ? (e) => canEditEvent(e as CalendarGridEvent) : undefined
                    }
                />
            </div>
        </div>
    );
};

export default TimeGridView;
